import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Formik, Form } from 'formik';
import {
  Container,
  Header,
  Button,
  Icon,
  Message,
  Segment,
  Grid,
  Divider,
  Table
} from 'semantic-ui-react';
import { pathTestingValidationSchema } from '../../validation/pathRegistrySchemas';
import {
  FormikInput,
  FormikField
} from '../../pages/subPages/voiceActor/FormComponents';
import { testPathResolution, clearError } from '../../slices/pathRegistrySlice';

import { AuthStdFooter } from "../../utils/auth-std-footer";
/**
 * PathTestingTool component for testing path resolution
 * @returns {JSX.Element} PathTestingTool component
 */
const PathTestingTool = () => {
  const dispatch = useDispatch();

  // Get path registry data from Redux
  const { testResult, loading, error } = useSelector(
    (state) => state.user.pathRegistry
  );

  // Local state
  const [formLoading, setFormLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setFormLoading(true);
    setLocalError(null);

    try {
      await dispatch(testPathResolution(values)).unwrap();
    } catch (err) {
      setLocalError(err.message || 'An error occurred while testing path resolution');
    } finally {
      setFormLoading(false);
      setSubmitting(false);
    }
  };

  // Clear error message
  const handleDismissError = () => {
    setLocalError(null);
    dispatch(clearError());
  };

  return (
    <div className="page-container" style={{ marginBottom: "70px" }}>
      <Container>
        <Segment>
          <Header as="h2">
            <Icon name="lab" />
            <Header.Content>
              Path Resolution Testing Tool
              <Header.Subheader>Test how paths are resolved to S3 locations</Header.Subheader>
            </Header.Content>
          </Header>
          <Divider />

          {/* Navigation buttons */}
          <Button
            as={Link}
            to="/admin/path-registry"
            icon
            labelPosition="left"
            style={{ marginBottom: '1em' }}
          >
            <Icon name="arrow left" />
            Back to Path Registry
          </Button>

          {/* Test form */}
          <Formik
            initialValues={{
              userId: '',
              path: ''
            }}
            validationSchema={pathTestingValidationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, isValid, dirty }) => (
              <Form>
                <Grid>
                  <Grid.Row>
                    <Grid.Column width={6}>
                      <FormikField
                        name="userId"
                        component={FormikInput}
                        label="User ID"
                        fluid
                        placeholder="Enter user ID"
                      />
                    </Grid.Column>
                    <Grid.Column width={10}>
                      <FormikField
                        name="path"
                        component={FormikInput}
                        label="Path to Test"
                        fluid
                        placeholder="e.g., aai-users/administrators/123/profile-img/avatar.jpg"
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Button
                        primary
                        type="submit"
                        loading={formLoading}
                        disabled={isSubmitting || !isValid || !dirty}
                        icon
                        labelPosition="left"
                      >
                        <Icon name="flask" />
                        Test Path Resolution
                      </Button>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Form>
            )}
          </Formik>

          {/* Error message */}
          {(error || localError) && (
            <Message
              negative
              onDismiss={handleDismissError}
              style={{ marginTop: '1em' }}
            >
              <Message.Header>Error</Message.Header>
              <p>{localError || error}</p>
            </Message>
          )}

          {/* Test results */}
          {testResult && (
            <Segment style={{ marginTop: '2em' }}>
              <Header as="h3" dividing>Test Results</Header>

              <Table definition>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell width={4}>Input Path</Table.Cell>
                    <Table.Cell>{testResult.resolvedParams?.path || '-'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>User ID</Table.Cell>
                    <Table.Cell>{testResult.resolvedParams?.userId || '-'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Request Type</Table.Cell>
                    <Table.Cell>{testResult.resolvedParams?.requestType || '-'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Folder Name</Table.Cell>
                    <Table.Cell>{testResult.resolvedParams?.folderName || '-'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>File Name</Table.Cell>
                    <Table.Cell>{testResult.resolvedParams?.fileName || '-'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Resolved S3 Key</Table.Cell>
                    <Table.Cell>
                      <code style={{
                        backgroundColor: '#f8f8f8',
                        padding: '0.5em',
                        borderRadius: '3px',
                        display: 'block',
                        overflowX: 'auto'
                      }}>
                        {testResult.s3Key || '-'}
                      </code>
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>

              <Message info style={{ marginTop: '1em' }}>
                <Message.Header>How Path Resolution Works</Message.Header>
                <p>
                  The path resolution process takes a path string and extracts parameters based on the matching path definition.
                  These parameters are then used to construct an S3 key using the folder template defined in the path definition.
                </p>
                <p>
                  The resolved S3 key is what will be used to access the object in the S3 bucket.
                </p>
              </Message>
            </Segment>
          )}
        </Segment>
      </Container>
      {AuthStdFooter()}
    </div>
  );
};

export default PathTestingTool;
